<template>
  <div class="bg-white shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] p-6 border-4 border-black">
    <div class="mb-6">
      <h3 class="mb-4 font-black text-2xl uppercase tracking-wider">
        🔊 TTS Settings
      </h3>

      <!-- TTS Mode Selection -->
      <div class="mb-6">
        <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
          TTS Mode
        </label>
        <div class="flex gap-4">
          <label class="flex items-center">
            <input type="radio" :value="true" v-model="localSettings.useServerTTS" :disabled="!isServerTTSAvailable"
              class="mr-2">
            <span class="font-medium text-gray-800">
              Server TTS (ElevenLabs)
              <span v-if="!isServerTTSAvailable" class="text-red-500 text-sm">
                (Unavailable)
              </span>
            </span>
          </label>
          <label class="flex items-center">
            <input type="radio" :value="false" v-model="localSettings.useServerTTS" class="mr-2">
            <span class="font-medium text-gray-800">Web Speech API</span>
          </label>
        </div>
      </div>

      <!-- Server TTS Settings -->
      <div v-if="localSettings.useServerTTS && isServerTTSAvailable" class="space-y-4">
        <!-- Voice Selection -->
        <div>
          <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
            Voice
          </label>
          <div class="flex gap-2">
            <select v-model="localSettings.selectedVoice"
              class="flex-1 bg-white px-3 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium">
              <option v-for="voice in elevenLabsVoices" :key="voice.voice_id" :value="voice.voice_id">
                {{ voice.name }}<span v-if="voice.category"> — {{ voice.category }}</span>
              </option>
            </select>
            <BrutalButton variant="secondary" size="sm" @click="previewCurrentVoice" :disabled="isSpeaking">
              {{ isSpeaking ? 'Playing...' : '🎵 Preview' }}
            </BrutalButton>
          </div>
        </div>

        <!-- Speech Rate -->
        <div>
          <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
            Speech Rate: {{ localSettings.speechRate }}
          </label>
          <input type="range" min="-50" max="100" step="10" :value="getRateValue(localSettings.speechRate)"
            @input="updateRate($event)" class="bg-gray-200 rounded-lg w-full h-2 appearance-none cursor-pointer">
          <div class="flex justify-between mt-1 text-gray-600 text-xs">
            <span>Slow (-50%)</span>
            <span>Normal (0%)</span>
            <span>Fast (+100%)</span>
          </div>
        </div>

        <!-- Speech Pitch -->
        <div>
          <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
            Speech Pitch: {{ localSettings.speechPitch }}
          </label>
          <input type="range" min="-200" max="200" step="25" :value="getPitchValue(localSettings.speechPitch)"
            @input="updatePitch($event)" class="bg-gray-200 rounded-lg w-full h-2 appearance-none cursor-pointer">
          <div class="flex justify-between mt-1 text-gray-600 text-xs">
            <span>Low (-200Hz)</span>
            <span>Normal (0Hz)</span>
            <span>High (+200Hz)</span>
          </div>
        </div>
      </div>

      <!-- Web Speech API Settings -->
      <div v-else-if="!localSettings.useServerTTS" class="space-y-4">
        <div>
          <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
            Web Speech Voice
          </label>
          <div class="flex gap-2">
            <select v-model="localSettings.selectedVoice"
              class="flex-1 bg-white px-3 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium">
              <option v-for="voice in voices" :key="voice.name" :value="voice">
                {{ voice.name }} ({{ voice.lang }})
              </option>
            </select>
            <BrutalButton variant="secondary" size="sm" @click="previewWebVoice" :disabled="isSpeaking">
              {{ isSpeaking ? 'Playing...' : '🎵 Preview' }}
            </BrutalButton>
          </div>
        </div>
      </div>

      <!-- Fallback Option -->
      <div class="mt-4">
        <label class="flex items-center">
          <input type="checkbox" v-model="localSettings.fallbackToWebSpeech" class="mr-2">
          <span class="font-medium text-gray-800 text-sm">
            Fallback to Web Speech API if server TTS fails
          </span>
        </label>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4 mt-6">
        <BrutalButton variant="primary" @click="saveSettings" class="flex-1">
          💾 Save Settings
        </BrutalButton>
        <BrutalButton variant="secondary" @click="resetSettings" class="flex-1">
          🔄 Reset to Defaults
        </BrutalButton>
      </div>

      <!-- Test Area -->
      <div class="bg-gray-50 mt-6 p-4 border-2 border-gray-300">
        <label class="block mb-2 font-bold text-gray-700 text-sm uppercase tracking-wide">
          Test TTS
        </label>
        <div class="flex gap-2">
          <input v-model="testText" type="text" placeholder="Enter text to test TTS..."
            class="flex-1 bg-white px-3 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium">
          <BrutalButton variant="primary" @click="testTTS" :disabled="isSpeaking || !testText.trim()">
            {{ isSpeaking ? 'Speaking...' : '▶️ Test' }}
          </BrutalButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useVoiceSynthesis } from '@/composables/useVoiceSynthesis'
import BrutalButton from '@/components/ui/BrutalButton.vue'

const {
  isSpeaking,
  isServerTTSAvailable,
  voices,
  elevenLabsVoices,
  settings,
  speak,
  previewVoice,
  updateSettings,
  initVoiceSynthesis
} = useVoiceSynthesis()

// Local settings for the component
const localSettings = ref({ ...settings.value })
const testText = ref("Hello, this is a test of the text-to-speech system.")

// Watch for changes in global settings
watch(settings, (newSettings) => {
  localSettings.value = { ...newSettings }
}, { deep: true })

// Watch for changes in selectedVoice and persist to settings
watch(() => localSettings.value.selectedVoice, (newVoice) => {
  updateSettings({ selectedVoice: newVoice })
})

// Helper functions for rate and pitch conversion
const getRateValue = (rate: string): number => {
  const match = rate.match(/^([+-]?\d+)%$/)
  return match ? parseInt(match[1]) : 0
}

const getPitchValue = (pitch: string): number => {
  const match = pitch.match(/^([+-]?\d+)Hz$/)
  return match ? parseInt(match[1]) : 0
}

const updateRate = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value)
  localSettings.value.speechRate = value >= 0 ? `+${value}%` : `${value}%`
}

const updatePitch = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value)
  localSettings.value.speechPitch = value >= 0 ? `+${value}Hz` : `${value}Hz`
}

const previewCurrentVoice = async () => {
  if (localSettings.value.selectedVoice) {
    await previewVoice(localSettings.value.selectedVoice)
  }
}

const previewWebVoice = async () => {
  if (localSettings.value.selectedVoice) {
    const testText = "Hello, this is a preview of this voice."
    await speak(testText, { voice: localSettings.value.selectedVoice })
  }
}

const testTTS = async () => {
  if (testText.value.trim()) {
    await speak(testText.value)
  }
}

const saveSettings = () => {
  updateSettings(localSettings.value)
  // Optional: Show success message
  console.log('TTS settings saved')
}

const resetSettings = () => {
  localSettings.value = {
    useServerTTS: true,
    selectedVoice: '21m00Tcm4TlvDq8ikWAM',
    speechRate: '+0%',
    speechPitch: '+0Hz',
    fallbackToWebSpeech: true
  }
  updateSettings(localSettings.value)
}

// Initialize on mount
onMounted(() => {
  initVoiceSynthesis()
})
</script>

<style scoped>
/* Custom slider styles */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #000;
  cursor: pointer;
  border: 2px solid #fff;
}

input[type="range"]::-moz-range-thumb {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #000;
  cursor: pointer;
  border: 2px solid #fff;
}

input[type="range"]::-webkit-slider-track {
  height: 8px;
  background: #ddd;
  border-radius: 4px;
}

input[type="range"]::-moz-range-track {
  height: 8px;
  background: #ddd;
  border-radius: 4px;
}
</style>